/**
 * Fit a single line of label text into a fixed width — vendor#1103.
 *
 * ## Why this exists rather than jsPDF's `maxWidth`
 *
 * `doc.text(s, x, y, { maxWidth })` **wraps**; it does not truncate. On a label
 * whose lines are drawn at fixed offsets, a wrapped second line renders exactly
 * where the next line is drawn, and the two overprint. That is not a styling
 * problem — it is the reason a printed label came back with the card name
 * running through the barcode reference above it.
 *
 * A label line is a *single* line by construction: there is one row of pixels
 * for it and no reflow. So the text is made to fit rather than allowed to
 * overflow.
 *
 * ## The order of sacrifice
 *
 * Size first, characters last. Shrinking keeps every word; ellipsising loses
 * some, and on a card label the tail carries the set code and the condition.
 * So: step down from `max` to `min`, and only if the text still does not fit at
 * `min` — where it is as small as it can be and still be read off a 203 dpi
 * thermal print — drop characters, with an ellipsis so the truncation is
 * visible rather than silent.
 *
 * A label that quietly prints half a card name is worse than one that shows it
 * was cut: the first is only discovered at the table.
 */
import type jsPDF from 'jspdf';

/** What {@link fitLines} decided. */
export interface FitLinesResult {
  /** The lines to draw, top to bottom. Never more than `maxLines`. */
  lines: string[];
  /** The size they all share. */
  fontSize: number;
  /** True when characters had to be dropped — the genuine last resort. */
  truncated: boolean;
}

/**
 * Fit text into at most `maxLines` rows, preferring a smaller size to a lost
 * character and a lost character to nothing — vendor#1105.
 *
 * ## The order of sacrifice, in full
 *
 * **Size, then a line, then characters.** #1103 shipped the first and third and
 * skipped the middle one, so a graded cert line lost its tail to an ellipsis
 * while 34 pt of blank label sat underneath it. On a 2 × 1 the usable height is
 * 60.5 pt and the tallest three-line block measures 26.3 pt — a second card line
 * is close to free, and it is strictly better than dropping the end of a cert
 * number.
 *
 * So: try one line, shrinking to `min`. Then try two (or `maxLines`), shrinking
 * again. Only if the text still will not fit does an ellipsis appear, and with
 * a second line available that is now genuinely rare rather than the usual
 * outcome.
 *
 * `singleLineMin` is deliberately higher than `min`: dropping to the absolute
 * floor on ONE line, to avoid a second line, is the wrong trade once the second
 * line is cheap.
 */
export function fitLines(
  doc: jsPDF,
  text: string,
  maxWidth: number,
  {
    max,
    min,
    maxLines = 2,
    singleLineMin,
    step = 0.25,
  }: FitTextOptions & { maxLines?: number; singleLineMin?: number },
): FitLinesResult {
  const restore = doc.getFontSize();
  try {
    if (maxWidth <= 0) return { lines: [], fontSize: min, truncated: text.length > 0 };
    if (text === '') return { lines: [], fontSize: max, truncated: false };

    // 1. One line, at the highest size that holds it.
    const oneLineFloor = singleLineMin ?? min;
    for (let size = max; size >= oneLineFloor; size -= step) {
      doc.setFontSize(size);
      if (doc.getTextWidth(text) <= maxWidth) {
        return { lines: [text], fontSize: size, truncated: false };
      }
    }

    // 2. Up to `maxLines`, at the highest size that holds them.
    for (let size = max; size >= min; size -= step) {
      doc.setFontSize(size);
      const split = doc.splitTextToSize(text, maxWidth) as string[];
      if (split.length <= maxLines) {
        return { lines: split, fontSize: size, truncated: false };
      }
    }

    // 3. Characters, finally. Keep the first rows whole and cut the last.
    doc.setFontSize(min);
    const split = doc.splitTextToSize(text, maxWidth) as string[];
    const kept = split.slice(0, maxLines);
    const lastIndex = kept.length - 1;
    const last = kept[lastIndex];
    if (last != null && split.length > maxLines) {
      // What did not fit is appended before cutting, so the ellipsis lands
      // where the text actually stops rather than at a wrap boundary that
      // happens to look complete.
      const remainder = split.slice(maxLines).join(' ');
      kept[lastIndex] = ellipsise(doc, `${last} ${remainder}`, maxWidth);
    }
    return { lines: kept, fontSize: min, truncated: split.length > maxLines };
  } finally {
    doc.setFontSize(restore);
  }
}

/** What {@link fitText} decided. */
export interface FitTextResult {
  /** The string to draw — the original, or a truncated one ending in `…`. */
  text: string;
  /** The size to draw it at, between `min` and `max`. */
  fontSize: number;
  /** True when characters had to be dropped. Callers may want to log it. */
  truncated: boolean;
}

/** Bounds for {@link fitText}. */
export interface FitTextOptions {
  /** Preferred size. Tried first. */
  max: number;
  /** Smallest legible size. Below this, characters go instead. */
  min: number;
  /** Step down granularity in points. Default 0.25. */
  step?: number;
}

const ELLIPSIS = '…';

/**
 * The largest size in `[min, max]` at which `text` fits `maxWidth`, or `min`
 * with the text ellipsised.
 *
 * Restores the document's font size before returning — it measures by setting
 * sizes, and leaving the last probe in place would silently restyle whatever
 * the caller drew next.
 */
export function fitText(
  doc: jsPDF,
  text: string,
  maxWidth: number,
  { max, min, step = 0.25 }: FitTextOptions,
): FitTextResult {
  const restore = doc.getFontSize();
  try {
    // A non-positive column can never fit anything. Returning `min` with the
    // text intact would draw it straight over whatever sits beside it, so this
    // reports an empty string: nothing is better than something illegible on
    // top of the barcode.
    if (maxWidth <= 0) return { text: '', fontSize: min, truncated: text.length > 0 };

    for (let size = max; size >= min; size -= step) {
      doc.setFontSize(size);
      if (doc.getTextWidth(text) <= maxWidth) {
        return { text, fontSize: size, truncated: false };
      }
    }

    doc.setFontSize(min);
    return { text: ellipsise(doc, text, maxWidth), fontSize: min, truncated: true };
  } finally {
    doc.setFontSize(restore);
  }
}

/**
 * Longest prefix of `text` that fits `maxWidth` once an ellipsis is appended.
 *
 * Binary search on the cut point rather than a character-at-a-time walk: a
 * label sheet is built one string at a time in the browser, and a 40-character
 * name at 5 pt would otherwise cost 40 text measurements per line.
 *
 * Assumes the font's advance widths are non-negative, so width grows
 * monotonically with the prefix — true of every font jsPDF ships.
 */
function ellipsise(doc: jsPDF, text: string, maxWidth: number): string {
  if (doc.getTextWidth(ELLIPSIS) > maxWidth) return '';

  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (doc.getTextWidth(text.slice(0, mid) + ELLIPSIS) <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  // Trailing spaces and separators before an ellipsis read as a typo
  // ("Charizard ex · …"), so the cut is tidied back to a word character.
  return text.slice(0, lo).replace(/[\s·\-,;:]+$/u, '') + ELLIPSIS;
}
